<script setup lang="ts">
import {
  tpgIconAddLocationAlt,
  tpgIconContentCopy,
  tpgIconExitToApp,
  tpgIconLocationDisabled,
  tpgIconMapTool,
  tpgIconMyLocation,
} from '@tpg/grammar-web-icons';
import {
  computed,
  onBeforeUnmount,
  onMounted,
  ref,
} from 'vue';
import { useI18n } from 'vue-i18n';

import { GrammarLoader, GrammarMenuItem, GrammarSectionHeader } from '@nora-monorepo/grammar-ui';
import { OperatingSystem } from '@nora-monorepo/nora-api-client';
import type { NoraCoordinateFormat } from '@nora-monorepo/shared';
import { formatLatLongCoordinate, useDeviceStore, useWebViewStore } from '@nora-monorepo/ui-shared';

import GrammarActionBox from '#nora-ui/components/action-box/GrammarActionBox.vue';
import { MapContextMenuAction } from '#nora-ui/map/components/controls/context-menu/map-context-menu.types';
import MapContextMenuPositionIcon from '#nora-ui/map/components/controls/context-menu/MapContextMenuPositionIcon.vue';
import type { GrammarActionBoxAction, GrammarActionBoxExpose } from '#nora-ui/map/components/controls/types';
import SymbolPreview from '#nora-ui/map/components/drawtools/symbol-tool/Symbol.vue';
import { useMapStore } from '#nora-ui/map/stores/map-store';

interface MapContextMenuProps {
  mapId: string;
}

interface CoordinateOption {
  format: NoraCoordinateFormat;
  labelKey: string;
}

const props = defineProps<MapContextMenuProps>();
const { t } = useI18n();

const LOCATION_PREVIEW_SIZE = 28;
const LOCATION_PREVIEW_CLEARANCE = 4;
const actionBoxOffset = LOCATION_PREVIEW_SIZE / 2 + LOCATION_PREVIEW_CLEARANCE;

const actionBoxRef = ref<GrammarActionBoxExpose | null>(null);
const actionBoxBoundary = ref<HTMLElement>();
const deviceStore = useDeviceStore();
const webViewStore = useWebViewStore();

const map = useMapStore().getMapByIdOrThrow(props.mapId);
const contextMenu = map.contextMenu;
const {
  canPlaceSymbol,
  clickedLocation,
  favoriteSymbols,
  hasLocation,
  isLoadingFavoriteSymbols,
} = contextMenu;
const {
  clearLocation,
  copyPosition,
  handleActionSelected,
  openInGoogleMaps,
  placeSymbol,
  setLocation,
} = contextMenu;

const actionBoxActions = computed<GrammarActionBoxAction[]>(() => {
  const actions: GrammarActionBoxAction[] = [
    {
      id: MapContextMenuAction.MapTools,
      label: t('mapContextMenu.actions.mapTools'),
      iconTemplate: tpgIconMapTool,
    },
    {
      id: MapContextMenuAction.CopyPosition,
      label: t('mapContextMenu.actions.copyPosition'),
      iconTemplate: tpgIconContentCopy,
    },
  ];

  if (canPlaceSymbol.value) {
    actions.unshift({
      id: MapContextMenuAction.PlaceSymbol,
      label: t('mapContextMenu.actions.placeSymbol'),
      iconTemplate: tpgIconAddLocationAlt,
    });
  }

  return actions;
});

const coordinateOptions: CoordinateOption[] = [
  { format: 'UTM', labelKey: 'mapContextMenu.coordinateFormats.utm' },
  { format: 'MGRS', labelKey: 'mapContextMenu.coordinateFormats.mgrs' },
  { format: 'WGS84', labelKey: 'mapContextMenu.coordinateFormats.dd' },
  { format: 'HDMM', labelKey: 'mapContextMenu.coordinateFormats.dmm' },
  { format: 'DMS', labelKey: 'mapContextMenu.coordinateFormats.dms' },
  {
    format: 'WEB_MERCATOR',
    labelKey: 'mapContextMenu.coordinateFormats.webMercator',
  },
];

// Maybe do this with tailwind instead?
const isActionBoxDocked = computed(() =>
  webViewStore.isWebView || deviceStore.operatingSystem !== OperatingSystem.WEB,
);

onMounted(() => {
  actionBoxBoundary.value = map.getOlMapOrThrow().getViewport();
  contextMenu.attach({
    onClose: () => actionBoxRef.value?.close(),
    onOpen: position => actionBoxRef.value?.open(position),
    onPositionChanged: position => actionBoxRef.value?.updatePosition(position),
  });
});

onBeforeUnmount(() => {
  contextMenu.detach();
});

defineExpose({
  hideMenu: () => contextMenu.hideMenu(),
  showMenu: (event: MouseEvent) => contextMenu.showMenu(event),
});
</script>

<template>
  <GrammarActionBox
    ref="actionBoxRef"
    :actions="actionBoxActions"
    :boundary="actionBoxBoundary"
    :docked="isActionBoxDocked"
    :offset="actionBoxOffset"
    @select="handleActionSelected"
  >
    <template #preview="{ position }">
      <MapContextMenuPositionIcon :position="position" />
    </template>

    <template #default="{ actionId }">
      <template v-if="actionId === MapContextMenuAction.PlaceSymbol">
        <GrammarSectionHeader
          header-type="menu-header"
          :label="t('mapContextMenu.actions.placeSymbol')"
        />
        <div
          v-if="isLoadingFavoriteSymbols"
          class="flex justify-center p-4"
        >
          <GrammarLoader />
        </div>
        <GrammarMenuItem
          v-else-if="favoriteSymbols.length === 0"
          disabled
          :label="t('mapContextMenu.noQuickAccessSymbols')"
        />
        <template v-else>
          <GrammarMenuItem
            v-for="symbol in favoriteSymbols"
            :key="symbol.id"
            :value="symbol.id"
            @select="placeSymbol(symbol)"
          >
            <template #content>
              <div class="flex w-48 max-w-[calc(100vw-4rem)] min-w-0 items-center gap-2">
                <SymbolPreview
                  :symbol="symbol"
                  class="h-5 w-5 shrink-0 object-contain"
                />
                <span class="truncate text-light-label-primary dark:text-dark-label-primary">
                  {{ symbol.name }}
                </span>
              </div>
            </template>
          </GrammarMenuItem>
        </template>
      </template>

      <template v-else-if="actionId === MapContextMenuAction.MapTools">
        <GrammarSectionHeader
          header-type="menu-header"
          :label="t('mapContextMenu.actions.mapTools')"
        />
        <GrammarMenuItem
          :label="t('mapContextMenu.setPosition')"
          :leading-icon-template="tpgIconMyLocation"
          @select="setLocation"
        />
        <GrammarMenuItem
          v-if="hasLocation"
          :label="t('mapContextMenu.clearPosition')"
          :leading-icon-template="tpgIconLocationDisabled"
          @select="clearLocation"
        />
        <GrammarMenuItem
          v-if="!webViewStore.isWebView"
          :label="t('mapContextMenu.openInGoogleMaps')"
          :trailing-icon-template="tpgIconExitToApp"
          @select="openInGoogleMaps"
        />
      </template>

      <template v-else-if="actionId === MapContextMenuAction.CopyPosition">
        <GrammarSectionHeader
          header-type="menu-header"
          :label="t('mapContextMenu.actions.copyPosition')"
        />
        <GrammarMenuItem
          v-for="option in coordinateOptions"
          :key="option.format"
          :label="clickedLocation ? formatLatLongCoordinate(clickedLocation, option.format) : ''"
          :secondary-label="t(option.labelKey)"
          :trailing-icon-template="tpgIconContentCopy"
          @select="copyPosition(option.format, option.labelKey)"
        />
      </template>
    </template>
  </GrammarActionBox>
</template>
